import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { toast } from 'sonner';
import { useAIConversationMessageFeedbackMutation } from '../../../queries/AIConversation/useAIConversationMessageFeedbackMutation';
import { useDeleteAIConversationMessageFeedbackMutation } from '../../../queries/AIConversation/useDeleteAIConversationMessageFeedbackMutation';
import { Button } from '../../../web/components-v2/Button';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { ThumbsDownIcon } from '../../../web/components/svgs/icons/ThumbsDownIcon';
import { ThumbsUpIcon } from '../../../web/components/svgs/icons/ThumbsUpIcon';
import { getRoleLabel } from './roleLabel';
import { transcriptMessageStyles } from './transcriptMessageStyles';

interface IProps {
	conversationId: string;
	message: Api.AIChatMessage;
}

/**
 * Derives the display text for a message robustly. Prefers the flat `text` field (how
 * SalesLiveCoaching conversational turns are populated); when that is absent, joins the text of
 * any `AITextContent` entries in `contents` (how the rich agent chat populates assistant turns).
 * Returns an empty string when neither yields text.
 */
const getMessageText = (message: Api.AIChatMessage): string => {
	if (message.text?.trim()) {
		return message.text;
	}

	const fromContents = (message.contents ?? [])
		.filter((content): content is Api.AITextContent => content._type === 'AITextContent')
		.map(content => content.text)
		.filter(Boolean)
		.join('\n\n');

	return fromContents;
};

/**
 * Formats a message's `creationDate` ISO string with the same `date-fns` format the neighboring
 * `ConversationList` uses, so timestamps read consistently across the page. Returns an empty
 * string for missing/unparseable values (the timestamp is then simply omitted).
 */
const formatTimestamp = (value?: string): string => {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return format(date, "MMM d, yyyy 'at' h:mm a");
};

/** Reads the exact token metadata fields the API resolver adds to assistant messages. */
const getTokenInfo = (
	message: Api.AIChatMessage
): {
	input?: number;
	output?: number;
	total?: number;
	cachedInput?: number;
	reasoning?: number;
	model?: string;
	provider?: string;
	hasAny: boolean;
} => {
	const input = message.inputTokenCount;
	const output = message.outputTokenCount;
	const total = message.totalTokenCount;
	const cachedInput = message.cachedInputTokenCount;
	const reasoning = message.reasoningTokenCount;

	const hasAny =
		typeof input === 'number' ||
		typeof output === 'number' ||
		typeof total === 'number' ||
		typeof cachedInput === 'number' ||
		typeof reasoning === 'number';

	return { input, output, total, cachedInput, reasoning, model: message.model, provider: message.provider, hasAny };
};

/**
 * Transcript row for the Coffee AI coaching review page. Unlike the shared
 * `AIConversationMessage` (which only renders an assistant turn's `contents` and ignores `text`
 * and the `System` role entirely — blanking SalesLiveCoaching transcripts), this always shows a
 * speaker, the resolved message text, a timestamp, per-message token usage, and feedback actions
 * for assistant messages.
 *
 * Rendered as a chat bubble: the AI Coach (assistant) sits on the left with a sparkle avatar; the
 * Rep (everything else, normally `User`) sits on the right with an "R" avatar and a tinted bubble.
 * `System` turns render as a centered divider instead of a bubble, matching the design.
 */
export function TranscriptMessage({ conversationId, message }: IProps) {
	const isSystem = message.role === Api.AIChatRole.System;
	const isCoach = message.role === Api.AIChatRole.Assistant;
	const text = getMessageText(message);
	const timestamp = formatTimestamp(message.creationDate);
	const [localFeedbackValue, setLocalFeedbackValue] = React.useState(message.feedback?.value);
	const resetLocalFeedback = () => setLocalFeedbackValue(message.feedback?.value);
	const feedbackMutation = useAIConversationMessageFeedbackMutation({
		onError: () => {
			resetLocalFeedback();
			toast.error('Failed to save feedback.');
		},
	});
	const deleteFeedbackMutation = useDeleteAIConversationMessageFeedbackMutation({
		onError: () => {
			resetLocalFeedback();
			toast.error('Failed to remove feedback.');
		},
	});
	const isFeedbackPending = feedbackMutation.isPending || deleteFeedbackMutation.isPending;

	React.useEffect(() => {
		setLocalFeedbackValue(message.feedback?.value);
	}, [message.feedback?.value]);

	const handleFeedback = (value: Api.AIChatMessageFeedback) => {
		if (isFeedbackPending || !message.id) {
			return;
		}

		if (localFeedbackValue === value) {
			setLocalFeedbackValue(undefined);
			deleteFeedbackMutation.mutate({ conversationId, messageId: message.id });
			return;
		}

		setLocalFeedbackValue(value);
		feedbackMutation.mutate({ conversationId, messageId: message.id, feedbackType: value });
	};

	// System turns (e.g. "Call started") read as a thread divider, not a speaker bubble.
	if (isSystem) {
		return (
			<div className={css(transcriptMessageStyles.system)}>
				<span className={css(transcriptMessageStyles.systemRule)} />
				<span className={css(transcriptMessageStyles.systemText)}>
					<PhoneIcon width={12} height={12} fill='currentColor' />
					{text || getRoleLabel(message.role)}
				</span>
				<span className={css(transcriptMessageStyles.systemRule)} />
			</div>
		);
	}

	const roleLabel = getRoleLabel(message.role);
	const tokens = getTokenInfo(message);

	const onlyTotal =
		typeof tokens.total === 'number' &&
		typeof tokens.input !== 'number' &&
		typeof tokens.output !== 'number' &&
		typeof tokens.cachedInput !== 'number' &&
		typeof tokens.reasoning !== 'number';
	const tokenParts = onlyTotal
		? [`${tokens.total} tokens`]
		: [
				typeof tokens.input === 'number' ? `↑ ${tokens.input} in` : null,
				typeof tokens.cachedInput === 'number' ? `${tokens.cachedInput} cached` : null,
				typeof tokens.output === 'number' ? `↓ ${tokens.output} out` : null,
				typeof tokens.reasoning === 'number' ? `${tokens.reasoning} reasoning` : null,
				typeof tokens.total === 'number' ? `${tokens.total} total` : null,
			].filter(Boolean);
	const modelLabel = [tokens.provider, tokens.model].filter(Boolean).join(' · ');
	const feedbackValue = localFeedbackValue;

	return (
		<div className={css(transcriptMessageStyles.row, !isCoach ? transcriptMessageStyles.rowRep : null)}>
			<div
				className={css(
					transcriptMessageStyles.avatar,
					isCoach ? transcriptMessageStyles.avatarCoach : transcriptMessageStyles.avatarRep
				)}
			>
				{isCoach ? <SparklesIcon className={css(transcriptMessageStyles.avatarIcon)} fill='currentColor' /> : 'R'}
			</div>
			<div className={css(transcriptMessageStyles.bubble, !isCoach ? transcriptMessageStyles.bubbleRep : null)}>
				<div className={css(transcriptMessageStyles.header, !isCoach ? transcriptMessageStyles.headerRep : null)}>
					<span
						className={css(
							transcriptMessageStyles.roleChip,
							isCoach ? transcriptMessageStyles.roleChipCoach : transcriptMessageStyles.roleChipRep
						)}
					>
						{roleLabel}
					</span>
					{timestamp ? <span className={css(transcriptMessageStyles.timestamp)}>{timestamp}</span> : null}
				</div>
				{text ? (
					<div className={css(transcriptMessageStyles.text)}>{text}</div>
				) : (
					<div className={css(transcriptMessageStyles.emptyText)}>(no content)</div>
				)}
				{tokenParts.length > 0 ? (
					<div className={css(transcriptMessageStyles.tokens)}>
						<span>{tokenParts.join(' · ')}</span>
						{modelLabel ? <span className={css(transcriptMessageStyles.model)}>{modelLabel}</span> : null}
					</div>
				) : null}
				{isCoach && message.id ? (
					<div className={css(transcriptMessageStyles.feedbackToolbar)}>
						<Button
							variant='ghost'
							size='icon'
							aria-label={feedbackValue === Api.AIChatMessageFeedback.ThumbsUp ? 'Remove thumbs up' : 'Give thumbs up'}
							title={feedbackValue === Api.AIChatMessageFeedback.ThumbsUp ? 'Remove thumbs up' : 'Give thumbs up'}
							disabled={isFeedbackPending}
							styles={[
								transcriptMessageStyles.feedbackButton,
								feedbackValue === Api.AIChatMessageFeedback.ThumbsUp && transcriptMessageStyles.feedbackButtonUpActive,
							]}
							onClick={() => handleFeedback(Api.AIChatMessageFeedback.ThumbsUp)}
						>
							<ThumbsUpIcon aria-hidden='true' />
						</Button>
						<Button
							variant='ghost'
							size='icon'
							aria-label={
								feedbackValue === Api.AIChatMessageFeedback.ThumbsDown ? 'Remove thumbs down' : 'Give thumbs down'
							}
							title={feedbackValue === Api.AIChatMessageFeedback.ThumbsDown ? 'Remove thumbs down' : 'Give thumbs down'}
							disabled={isFeedbackPending}
							styles={[
								transcriptMessageStyles.feedbackButton,
								feedbackValue === Api.AIChatMessageFeedback.ThumbsDown &&
									transcriptMessageStyles.feedbackButtonDownActive,
							]}
							onClick={() => handleFeedback(Api.AIChatMessageFeedback.ThumbsDown)}
						>
							<ThumbsDownIcon aria-hidden='true' />
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
}
